// vollmachtNachweise.ts — „Vollmachten ansehen" im Aktenkopf (PO 01.10.2026).
//
// Ersetzt „Nachweis öffnen" (T-17): Statt nur der eigenen Erstinformation
// listet der Menüpunkt ALLE vorliegenden (hochgeladenen oder unterschriebenen)
// Vollmachten des Kontakts und seines Verbunds. Verbund = dieselbe Quelle wie
// „Für alle im Verbund generieren" (`mitgliederAusUmfeld(…, "verbund", …)`:
// bei einer Firma die Verbundfirmen, bei einer Person ihre Unternehmen).
//
// Ein Klick öffnet das Dokument im KMVS-PDF-Viewer in einem neuen Tab
// (`/dokumente/:id/pdf`, `PdfDokumentPage`) — nicht die nackte Datei im
// Browser-PDF. Nur ein Altbestand-Scan ohne Dokument (Medienpfad) hat keine
// Dokument-ID; der geht weiter über den Blob-Tab.

import { VOLLMACHT_STANDARD_KEY } from "@kmvs/forms-core";
import type { ContactAuthority } from "../../types/Contact";
import { vollmachtLabel, type VollmachtDto } from "../vollmachten/vollmachtTypes";

export type VollmachtNachweis = {
  /** Stabiler Schlüssel für Listen. */
  key: string;
  art: string;
  artLabel: string;
  contactId: number;
  /** Nur bei Verbund-Einträgen gesetzt — beim Kontakt selbst steht kein Name. */
  kontaktName?: string;
  /** ISO-Zeitpunkt (unterschrieben bzw. hochgeladen), beim Altbestand unbekannt. */
  datum: string | null;
  ziel: { documentId: number } | { url: string };
  altbestand?: boolean;
};

const liegtVor = (v: VollmachtDto): boolean => v.status === "unterschrieben" || v.status === "hochgeladen";

/**
 * Vorliegende Vollmachten eines Kontakts. `kontaktName` nur für Verbund-Einträge.
 * Der Altbestand der Erstinformation (`ContactAuthority`) zählt mit, solange es
 * keine neue Fassung der Erstinformation gibt.
 */
export function nachweiseAusVollmachten(
  contactId: number,
  liste: VollmachtDto[],
  { kontaktName, authority }: { kontaktName?: string; authority?: ContactAuthority | null } = {},
): VollmachtNachweis[] {
  const aus: VollmachtNachweis[] = [];
  for (const v of liste) {
    if (!liegtVor(v)) continue;
    // Reihenfolge wie beim PDF-Abruf im Backend: unterschrieben > hochgeladen.
    const docId = v.signedDocumentId ?? v.uploadedDocumentId ?? null;
    if (docId == null) continue;
    aus.push({
      key: `${contactId}:${v.id}`,
      art: v.art,
      artLabel: vollmachtLabel(v.art),
      contactId,
      ...(kontaktName ? { kontaktName } : {}),
      datum: v.unterschriebenAt ?? v.hochgeladenAt ?? null,
      ziel: { documentId: docId },
    });
  }
  const erstinfoNeu = liste.some((v) => v.art === VOLLMACHT_STANDARD_KEY && liegtVor(v));
  if (!erstinfoNeu && authority?.erstinformation) {
    const url = (authority.documentUrl ?? "").trim();
    const ziel = authority.documentId != null ? { documentId: authority.documentId } : url ? { url } : null;
    if (ziel) {
      aus.push({
        key: `${contactId}:altbestand`,
        art: VOLLMACHT_STANDARD_KEY,
        artLabel: vollmachtLabel(VOLLMACHT_STANDARD_KEY),
        contactId,
        ...(kontaktName ? { kontaktName } : {}),
        datum: null,
        ziel,
        altbestand: true,
      });
    }
  }
  return aus;
}

/** Route des KMVS-PDF-Viewers im eigenen Tab (`PdfDokumentPage`, App.tsx). */
export const kmvsPdfRoute = (documentId: number): string => `/dokumente/${documentId}/pdf`;

const datumText = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/** „Maklervollmacht · Quip GmbH · 12.09.2026" — Kontakt nur bei Verbund. */
export function nachweisText(n: VollmachtNachweis): string {
  return [n.artLabel, n.kontaktName, datumText(n.datum) ?? (n.altbestand ? "bisheriger Stand" : null)]
    .filter(Boolean)
    .join(" · ");
}
